import { ConfirmModal } from '@monorepo/shared/componentsV2/modals/ConfirmModal';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ConfirmModalOpts,
  ModalContext,
  ModalContextValue,
} from './ModalContext';

type ModalOpts = ConfirmModalOpts;

type ModalState = {
  modalOpts?: ModalOpts;
};

type ModalProviderProps = {
  children: NonNullable<React.ReactNode>;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export function ModalProvider({ children }: ModalProviderProps) {
  const [modalState, setModalState] = useState<ModalState>({
    modalOpts: undefined,
  });
  const { modalOpts } = modalState;

  const setModalOpen = useCallback((opts: ModalOpts) => {
    setModalState(() => ({
      modalOpts: opts,
    }));
  }, []);

  const setModalClosedAndReset = useCallback(() => {
    setModalState(() => ({
      modalOpts: undefined,
    }));
  }, []);

  const promiseCallbacksRef = useRef<{
    resolve: (isConfirmed: boolean) => void;
    reject: (reason?: unknown) => void;
  }>({
    resolve: noop,
    reject: noop,
  });

  const openModal = useCallback(
    (opts: ModalOpts): Promise<boolean> => {
      setModalOpen(opts);
      const p = new Promise<boolean>((resolve, reject) => {
        promiseCallbacksRef.current = { resolve, reject };
      }).finally(() => {
        promiseCallbacksRef.current = {
          resolve: noop,
          reject: noop,
        };
        setModalClosedAndReset();
      });
      return p;
    },
    [setModalOpen, setModalClosedAndReset],
  );

  const confirm = useCallback(
    (opts: ConfirmModalOpts) => openModal(opts),
    [openModal],
  );

  const contextValue: ModalContextValue = useMemo(
    () => ({ confirm }),
    [confirm],
  );

  const { resolve, reject } = promiseCallbacksRef.current;
  const onConfirmAsync = useCallback(
    async (isConfirmed: boolean) => {
      // by default, this will simply resolve the modal (closes it)
      // but if an onConfirmAsync function is passed to it, we await it and then resolve the modal
      try {
        if (modalOpts?.onConfirmAsync) {
          await modalOpts.onConfirmAsync(isConfirmed);
        }
        resolve(isConfirmed);
      } catch (err) {
        reject(err);
      }
    },
    [modalOpts, reject, resolve],
  );

  const modalElements = useMemo(() => {
    if (!modalOpts) return null;
    // It doesn't matter which label we use as a default here since a caller will provide their own label
    // if their on-confirm operation is async, i.e. they pass `onConfirmAsync` option
    const { afterConfirmButtonText = 'Processing' } = modalOpts;
    return (
      <ConfirmModal
        description={modalOpts.description}
        title={modalOpts.title}
        showCloseButton={modalOpts.showCloseButton}
        open
        cancelButtonText={modalOpts.cancelButtonText}
        confirmButtonText={modalOpts.confirmButtonText}
        afterConfirmButtonText={afterConfirmButtonText}
        danger={modalOpts.danger}
        onConfirmAsync={onConfirmAsync}
        onCancel={() => resolve(false)}
      />
    );
  }, [modalOpts, resolve, onConfirmAsync]);

  return (
    <ModalContext.Provider value={contextValue}>
      {modalElements}
      {children}
    </ModalContext.Provider>
  );
}
